import { translateBackend } from '@assets/i18n/config';
import { FeatureFlagTypes } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { AccountTypes } from '@interfaces/account';
import type { RepCode } from '@interfaces/repCode';
import Search from '@mui/icons-material/Search';
import { InputAdornment, TextField } from '@mui/material';
import { Badge } from '@ovComponents/2-component/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { RepCodeSelect } from '@ovComponents/3-pattern/repCodeSelect/repCodeSelect';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { AnalyticsContext } from '@providers/analyticsProvider';
import {
  useFeatureFlag,
  useOrganizationFeatureFlag,
} from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Button, Typography, useTheme } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  CreateAccountForGoalArgs,
  CreateAccountForGoalOptions,
} from './createAccountForGoal';

type AccountListItem = {
  weight: number;
  type: string;
};

// custom weighted list by Josh
const generateWeightedList = (accountTypeList: string[]): AccountListItem[] => {
  const weightedList = accountTypeList.map((accountItem: string) => {
    if (accountItem === AccountTypes.PERSONAL) {
      return {
        weight: 1,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.CASH_JOINT) {
      return {
        weight: 2,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.TFSA) {
      return {
        weight: 3,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RRSP) {
      return {
        weight: 4,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RRSP_SPOUSAL) {
      return {
        weight: 5,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_FAMILY) {
      return {
        weight: 6,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_SINGLE) {
      return {
        weight: 7,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_ADULT) {
      return {
        weight: 8,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_FAMILY_JOINT) {
      return {
        weight: 9,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_SINGLE_JOINT) {
      return {
        weight: 10,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RRIF) {
      return {
        weight: 11,
        type: accountItem,
      };
    }
    return {
      weight: 15,
      type: accountItem,
    };
  });
  return weightedList.sort((a, b) => a.weight - b.weight);
};

export const CreateAccountForGoalVisual = ({
  options,
  continueFunc,
  accountType,
  suggestedTypes,
  otherTypes,
  workflowCompletion,
  loading,
  saving = false,
  activeAccountsByType: accountsByType = {},
  getReusableRespTransferValue,
  willPromptAccountCreationMethodChoice,
  confirmationDialog,
  accountCreationMethodChoiceDialog,
  selectedRepCode,
  setSelectedRepCode,
  repCodeError,
  setRepCodeError,
}: {
  options: CreateAccountForGoalOptions;
  accountType: string;
  continueFunc: (
    createAccountForGoalArgs: CreateAccountForGoalArgs,
  ) => Promise<void>;
  suggestedTypes: AccountTypes[];
  otherTypes: AccountTypes[];
  workflowCompletion?: any;
  loading: boolean;
  saving?: boolean;
  activeAccountsByType?: Record<string, Account[]>;
  getReusableRespTransferValue?: (accountType: string) => boolean;
  willPromptAccountCreationMethodChoice?: (accountType: string) => boolean;
  confirmationDialog?: React.ReactNode;
  accountCreationMethodChoiceDialog?: React.ReactNode;
  selectedRepCode?: RepCode;
  setSelectedRepCode?: (repCode: RepCode | undefined) => void;
  repCodeError?: boolean;
  setRepCodeError?: (error: boolean) => void;
}) => {
  const { t } = useTranslation([
    'client',
    'accountTypes',
    'accountsDetail',
    'workflowCompletions',
  ]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedComponent, setSelectedComponent] = useState<
    'SUGGESTED' | 'OTHER' | 'DEFAULT'
  >('DEFAULT');
  const [suggestedList, setSuggestedList] = useState<string[]>([]);
  const [otherList, setOtherList] = useState<string[]>([]);
  const [respDialogType, setRespDialogType] = useState<string | null>(null);
  const [
    isTransferredFromAnotherInstitution,
    setIsTransferredFromAnotherInstitution,
  ] = useState(false);
  const hasCreateAccountOrSubAccountConfig = useOrganizationFeatureFlag(
    FeatureFlagTypes.CreateAccountOrSubAccountConfig,
  );
  const isRespInstitutionTransferEnabled = useFeatureFlag(
    FlagsmithFlags.resp_transferred_from_another_institution,
  );

  useEffect(() => {
    const bySearchTerm = (type: string) => {
      const techHaystack = (type ?? '').toUpperCase();
      const translatedHaystack = t(`accountTypes:${type}`).toUpperCase();
      const needle = searchQuery.trim().toUpperCase();
      return (
        techHaystack.includes(needle) || translatedHaystack.includes(needle)
      );
    };

    setSuggestedList(suggestedTypes.filter(bySearchTerm));
    setOtherList(otherTypes.filter(bySearchTerm));
  }, [searchQuery, t, suggestedTypes, otherTypes]);

  const onTileSelect = async (e: any) => {
    if (loading || saving) return;
    setSelectedComponent('SUGGESTED');
    sendAnalytic(
      ovAnalyticsEvents.workflowsCreateAccountForGoalContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );
    const selectedAccountType = e.target.value;
    if (
      isRespInstitutionTransferEnabled &&
      String(selectedAccountType).includes('RESP') &&
      // Skip the standalone dialog when the account-creation-method dialog will handle the
      // transfer toggle; there the account isn't chosen yet, so pre-filling here would be
      // ambiguous and could clobber an existing account's flag on confirm.
      !willPromptAccountCreationMethodChoice?.(selectedAccountType)
    ) {
      // Pre-fill with the value already persisted on the account being reused so the user
      // sees the current DB state rather than a hard-coded default.
      setIsTransferredFromAnotherInstitution(
        getReusableRespTransferValue?.(selectedAccountType) ?? false,
      );
      setRespDialogType(selectedAccountType);
      return;
    }
    await continueFunc({ accountType: selectedAccountType });
  };

  const confirmRespAccountCreation = async () => {
    const selectedAccountType = respDialogType;
    setRespDialogType(null);
    if (!selectedAccountType) return;
    await continueFunc({
      accountType: selectedAccountType as AccountTypes,
      isTransferredFromAnotherInstitution,
    });
  };

  if (!options) return <></>;

  const searchQueryExists = searchQuery.trim() !== '';

  const asValueLabelSubtitle = (
    a: AccountListItem,
  ): React.ComponentProps<typeof SelectionTile>['options'][number] => {
    const existingAccountsCount = accountsByType[a.type]?.length || 0;
    return {
      value: a.type,
      label: t(`accountTypes:${a.type}`),
      subtitle: options.displayAccountDefinitions
        ? t(`accountsDetail:accountDescriptions.${a.type}`)
        : undefined,
      labelSideComponent:
        existingAccountsCount > 0 ? (
          hasCreateAccountOrSubAccountConfig ? (
            <Badge
              label={t(
                'workflowCompletions:createAccountForGoal.numAccountsBadge',
                { numAccounts: existingAccountsCount },
              )}
            />
          ) : undefined
        ) : undefined,
    };
  };

  return (
    <Form>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      {!loading && (
        <>
          {setSelectedRepCode && (
            <RepCodeSelect
              repCode={selectedRepCode}
              setRepCode={(repCode) => {
                setSelectedRepCode(repCode);
                if (repCode) setRepCodeError?.(false);
              }}
              label={t('workflowCompletions:createAccount.repCodeLabel')}
              testId='create-account-for-goal-rep-code'
              error={repCodeError}
              errorText={t('workflowCompletions:createAccount.repCodeRequired')}
              fullWidth
              sx={{ mb: 3 }}
            />
          )}
          <TextField
            value={searchQuery}
            data-testid='search-accounts'
            label=''
            fullWidth
            size='small'
            placeholder={t('accountsDetail:searchAccounts')}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'data-testid': 'fulltextsearch-select' }}
            sx={{ mb: 2 }}
            disabled={saving}
          />

          {!searchQueryExists && suggestedList.length > 0 && (
            <Typography variant='headingSmall' sx={{ mb: 3, fontWeight: 900 }}>
              {translateBackend(options.commonTypesLabel)}
            </Typography>
          )}
          <SelectionTile
            testId={'account-type-suggested'}
            onChange={onTileSelect}
            value={accountType}
            options={generateWeightedList(suggestedList).map(
              asValueLabelSubtitle,
            )}
            type='completion'
            selectedComponent={selectedComponent}
            component='SUGGESTED'
            disabled={saving}
          />

          {!searchQueryExists && suggestedList.length > 0 && (
            <Typography
              variant='headingSmall'
              sx={{ mb: 3, mt: 3, fontWeight: 900 }}
            >
              {t('accountsDetail:otherAccountTypes')}
            </Typography>
          )}
          <SelectionTile
            testId='account-type'
            onChange={onTileSelect}
            value={accountType}
            options={generateWeightedList(otherList).map(asValueLabelSubtitle)}
            type='completion'
            selectedComponent={selectedComponent}
            component='OTHER'
            disabled={saving}
          />

          {suggestedList.length === 0 && otherList.length === 0 && (
            <Typography
              variant='bodyLarge'
              sx={{ color: theme.color.onSurfaceVariant }}
            >
              {t('accountsDetail:noAccountOptions')}
            </Typography>
          )}
        </>
      )}
      {confirmationDialog}
      {accountCreationMethodChoiceDialog}
      <Dialog
        open={!!respDialogType}
        onClose={() => setRespDialogType(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle onClose={() => setRespDialogType(null)}>
          {t('workflowCompletions:respAccountCreation.transferDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t(
              'workflowCompletions:respAccountCreation.transferDialogAccountTypeLabel',
            )}
            value={respDialogType ? t(`accountTypes:${respDialogType}`) : ''}
            fullWidth
            size='small'
            InputProps={{ readOnly: true }}
            sx={{ mb: 3 }}
          />
          <SwitchField
            data-testid='resp-transferred-from-another-institution'
            label={t(
              'workflowCompletions:respAccountCreation.transferredFromAnotherInstitution',
            )}
            checked={isTransferredFromAnotherInstitution}
            onChange={setIsTransferredFromAnotherInstitution}
          />
        </DialogContent>
        <DialogFooter>
          <Button
            data-testid='resp-create-new-account'
            fullWidth
            loading={saving}
            disabled={saving}
            onClick={confirmRespAccountCreation}
          >
            {t('workflowCompletions:respAccountCreation.createNewAccount')}
          </Button>
        </DialogFooter>
      </Dialog>
    </Form>
  );
};

export default CreateAccountForGoalVisual;
